// src/EmailLandingPage.js
import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Box, Typography, Button, Alert } from '@mui/material';
import FacebookIcon from '@mui/icons-material/Facebook';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import API_URL, { API_BASE_URL } from './config';

const REDIRECT_URL = 'https://aplikacja.ewadia.pl/#/wadium/wadium-application?from=smart';
const COUNTDOWN_SECONDS = 0; // Wyłączone na czas developmentu (ustaw na 10 dla produkcji)
const ENABLE_COUNTDOWN = false; // Wyłączone na czas developmentu (ustaw na true dla produkcji)

const EmailLandingPage = () => {
    const { token } = useParams();
    const [searchParams] = useSearchParams();
    const [visitLogged, setVisitLogged] = useState(false);
    const [countdown, setCountdown] = useState(COUNTDOWN_SECONDS);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    
    const redirectToEwadia = () => {
        window.location.href = REDIRECT_URL;
    };
    
    useEffect(() => {
        // Register visit and consent when component mounts
        const registerVisitAndConsent = async () => {
            if (!token) {
                setError('Brak tokena w adresie URL');
                setLoading(false);
                return;
            }
            
            try {
                setLoading(true);
                setError(null);
                
                // Collect all query parameters
                const queryParams = {};
                searchParams.forEach((value, key) => {
                    queryParams[key] = value;
                });
                
                // Collect device fingerprint with URL information
                const deviceFingerprint = {
                    screen_resolution: `${window.screen.width}x${window.screen.height}`,
                    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                    language: navigator.language || navigator.userLanguage,
                    platform: navigator.platform,
                    color_depth: window.screen.colorDepth,
                    viewport: `${window.innerWidth}x${window.innerHeight}`,
                    full_url: window.location.href,
                    pathname: window.location.pathname,
                    search: window.location.search
                };
                
                const response = await fetch(`${API_BASE_URL}/email/landing-page-consent/${token}/`, {
                    method: 'POST',
                    headers: {
                        'Accept': 'application/json',
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        query_params: queryParams,
                        device_fingerprint: deviceFingerprint,
                    }),
                });
                
                if (response.ok) {
                    const data = await response.json();
                    setVisitLogged(true);
                } else if (response.status === 404) {
                    const errorData = await response.json().catch(() => ({}));
                    setError(errorData.error || 'Nieprawidłowy lub wygasły link. Nie można zlokalizować oferenta.');
                } else {
                    const errorData = await response.json().catch(() => ({}));
                    setError(errorData.error || 'Wystąpił błąd podczas rejestracji zgody.');
                }
            } catch (error) {
                console.error('Failed to register visit and consent:', error);
                setError('Wystąpił błąd podczas połączenia z serwerem.');
            } finally {
                setLoading(false);
            }
        };
        
        registerVisitAndConsent();
    }, [token, searchParams]);
    
    useEffect(() => {
        // Countdown timer - wyłączony podczas developmentu
        if (!ENABLE_COUNTDOWN) {
            return; // Nie uruchamiaj timera jeśli jest wyłączony
        }
        
        if (countdown > 0) {
            const timer = setTimeout(() => {
                setCountdown(countdown - 1);
            }, 1000);
            return () => clearTimeout(timer);
        } else {
            // Redirect when countdown reaches 0
            redirectToEwadia();
        }
    }, [countdown]);

    // Show error message if bidder not found or other error
    if (error) {
        return (
            <Box 
                sx={{
                    minHeight: '100vh',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    paddingTop: '5vh',
                    backgroundImage: 'linear-gradient(135deg, #1a237e 0%, #121858 100%)',
                }}
            >
                <Box
                    sx={{
                        width: '100%',
                        maxWidth: '600px',
                        backgroundColor: '#fff',
                        padding: '40px',
                        borderRadius: '8px',
                        boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                    }}
                >
                    <Box
                        component="img"
                        src="/logo.svg"
                        alt="Polfund Logo"
                        sx={{
                            width: 260,
                            marginBottom: 4,
                        }}
                    />
                    
                    <Alert severity="error" sx={{ width: '100%', mb: 3 }}>
                        {error}
                    </Alert>

                    <Button
                        variant="contained"
                        onClick={redirectToEwadia}
                        sx={{
                            padding: '12px 32px',
                            backgroundColor: '#E31837',
                            '&:hover': {
                                backgroundColor: '#FF1F3F',
                            },
                            fontSize: '1rem',
                            fontWeight: 500,
                        }}
                    >
                        Przejdź do ewadia.pl
                    </Button>
                </Box>
            </Box>
        );
    }

    // Show loading state
    if (loading) {
        return (
            <Box 
                sx={{
                    minHeight: '100vh',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    paddingTop: '5vh',
                    backgroundImage: 'linear-gradient(135deg, #1a237e 0%, #121858 100%)',
                }}
            >
                <Box
                    sx={{
                        width: '100%',
                        maxWidth: '600px',
                        backgroundColor: '#fff',
                        padding: '40px',
                        borderRadius: '8px',
                        boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                    }}
                >
                    <Box
                        component="img"
                        src="/logo.svg"
                        alt="Polfund Logo"
                        sx={{
                            width: 260,
                            marginBottom: 4,
                        }}
                    />
                    
                    <Typography 
                        variant="body1" 
                        sx={{ 
                            color: '#666666', 
                            textAlign: 'center',
                        }}
                    >
                        Przetwarzanie...
                    </Typography>
                </Box>
            </Box>
        );
    }

    return (
        <Box 
            sx={{
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'flex-start',
                paddingTop: '5vh',
                backgroundImage: 'linear-gradient(135deg, #1a237e 0%, #121858 100%)',
            }}
        >
            <Box
                sx={{
                    width: '100%',
                    maxWidth: '750px',
                    backgroundColor: '#fff',
                    padding: '40px',
                    borderRadius: '8px',
                    boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                }}
            >
                <Box
                    component="img"
                    src="/logo.svg"
                    alt="Polfund Logo"
                    sx={{
                        width: 260,
                        marginBottom: 4,
                    }}
                />
                
                <Typography 
                    variant="h4" 
                    component="h1"
                    sx={{ 
                        mb: 3, 
                        color: '#333333',
                        textAlign: 'center',
                        fontWeight: 500,
                    }}
                >
                    Dziękujemy za udzieloną zgodę
                </Typography>

                <Typography 
                    variant="body1" 
                    sx={{ 
                        mb: 6, 
                        color: '#666666', 
                        textAlign: 'center',
                        lineHeight: 1.6,
                    }}
                >
                    Właśnie otrzymałeś darmowy limit do 100 tys. złotych na poręczenia wadialne. Aby skorzystać z limitu złóż wniosek poprzez system www.ewadia.pl lub skontaktuj się z naszym zespołem pod adresem e-mail
                </Typography>

                {ENABLE_COUNTDOWN && (
                    <Typography 
                        variant="body1" 
                        sx={{ 
                            color: '#666666', 
                            textAlign: 'center',
                            mb: 3,
                            fontWeight: 500,
                        }}
                    >
                        Przekierowanie za {countdown}s...
                    </Typography>
                )}

                <Box
                    sx={{
                        display: 'flex',
                        gap: 3,
                        width: '100%',
                        justifyContent: 'center',
                        flexWrap: 'nowrap',
                        mt: 2,
                    }}
                >
                    <Button
                        variant="contained"
                        onClick={() => {
                            window.location.href = 'https://ewadia.pl/kontakt/';
                        }}
                        sx={{
                            padding: '16px 32px',
                            backgroundColor: '#E31837',
                            borderRadius: '8px',
                            '&:hover': {
                                backgroundColor: '#FF1F3F',
                            },
                            fontSize: '1rem',
                            fontWeight: 500,
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1,
                        }}
                    >
                        <Typography sx={{ color: 'white', fontWeight: 500 }}>
                            Skontaktuj się z Nami
                        </Typography>
                        
                    </Button>

                    <Button
                        variant="contained"
                        onClick={redirectToEwadia}
                        sx={{
                            padding: '16px 32px',
                            backgroundColor: '#E31837',
                            borderRadius: '8px',
                            '&:hover': {
                                backgroundColor: '#FF1F3F',
                            },
                            fontSize: '1rem',
                            fontWeight: 500,
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            textAlign: 'center',
                        }}
                    >
                        <Typography sx={{ color: 'white', fontWeight: 500 }}>
                            Aktywuj darmowy limit 100 tys.
                        </Typography>
                        <Typography sx={{ color: 'white', fontWeight: 500 }}>
                            na wadium
                        </Typography>
                    </Button>
                </Box>
            </Box>
        </Box>
    );
};

export default EmailLandingPage;

